import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { themevals } from "theme/themevals";
import { attachProposalToLead } from "api/Proposals";
import { getLeadSettings, createLead } from "api/Leads";
import LeadPicker from "containers/Leads/LeadPicker";

/**
 * The lead a proposal belongs to, on the proposal detail page (s.6).
 *
 * Attached: "Lead: <org> - <contact>" linking to the lead, with Change and
 * Detach. Not attached: search for a lead (LeadPicker - it used to be a
 * <select> of the first 100 leads by name, so most leads could not be
 * picked; 2026-09-28), or create one from the prospect name. Works on
 * drafts and sent proposals alike; the document the prospect sees does
 * not change. Hidden when the module is off, and for proposals that
 * belong to a real client (those stay with the client).
 */
export default function LeadAttachBar({
  proposal,
  onChanged,
}: {
  proposal: { id: string; prospectName: string; client?: { id: string; name: string; stage?: string; contactName?: string | null } | null };
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getLeadSettings().then((s) => setEnabled(!!s?.enabled));
  }, []);

  const client = proposal.client;
  if (!enabled) return null;
  if (client?.id && client.stage && client.stage !== "lead") return null;

  const run = async (leadId: string | null) => {
    setBusy(true);
    setError(null);
    const res: any = await attachProposalToLead(proposal.id, leadId);
    setBusy(false);
    if (res?.error) return setError(res.error);
    setEditing(false);
    onChanged();
  };

  const createAndAttach = async () => {
    setBusy(true);
    setError(null);
    const made: any = await createLead({ name: proposal.prospectName.trim(), leadSource: "proposal_generator" });
    if (made?.error) {
      setBusy(false);
      return setError(made.error);
    }
    await run(made.id);
  };

  const bar: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontFamily: "Poppins",
    padding: "10px 14px", marginBottom: 16, background: "#FAFAF8", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 8, fontSize: 13,
  };
  const link: React.CSSProperties = { background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0, fontFamily: "Poppins", fontSize: 12, fontWeight: 500 };

  return (
    <div style={bar}>
      {client?.id && !editing ? (
        <>
          <span>
            Lead:{" "}
            <a href={`/leads/${client.id}`} style={{ color: "#185FA5", fontWeight: 600 }} onClick={(e) => { e.preventDefault(); navigate(`/leads/${client.id}`); }}>
              {client.name}
              {client.contactName ? ` \u2014 ${client.contactName}` : ""}
            </a>
          </span>
          <span style={{ color: themevals.colors.dkgrey, fontSize: 12 }}>opens, replies and follow-ups show on the lead</span>
          <span style={{ flex: 1 }} />
          <button type="button" style={link} onClick={() => setEditing(true)}>Change</button>
          <button type="button" style={{ ...link, color: "#A60000" }} disabled={busy} onClick={() => run(null)}>Detach</button>
        </>
      ) : !editing ? (
        <>
          <span style={{ color: "#633806", fontWeight: 600 }}>Not attached to a lead</span>
          <span style={{ color: themevals.colors.dkgrey, fontSize: 12 }}>its opens and replies are not on anyone&rsquo;s queue</span>
          <span style={{ flex: 1 }} />
          <button type="button" style={link} onClick={() => setEditing(true)}>Attach to a lead</button>
        </>
      ) : (
        <>
          <div style={{ flex: "1 1 360px", minWidth: 280 }}>
            <LeadPicker value={null} suggestFor={proposal.prospectName} autoFocus onChange={(l) => { if (l && !busy) run(l.id); }} />
          </div>
          <span style={{ color: themevals.colors.dkgrey }}>or</span>
          <button type="button" style={link} disabled={busy} onClick={createAndAttach}>Create a lead &ldquo;{proposal.prospectName}&rdquo; and attach</button>
          <span style={{ flex: 1 }} />
          <button type="button" style={{ ...link, color: themevals.colors.dkgrey }} onClick={() => { setEditing(false); setError(null); }}>Cancel</button>
        </>
      )}
      {busy && <div style={{ width: "100%", fontSize: 12, color: themevals.colors.dkgrey }}>{"Saving\u2026"}</div>}
      {error && <div style={{ width: "100%", color: "#A60000", fontSize: 12 }}>{error}</div>}
    </div>
  );
}
